import { removeHtmlTags } from '@portal/utils/util-format';
import { guardUnspecified } from '@portal/utils/util-guards';

import { getResponsiveImageData } from '@jtnews/shared/images';
import { addUrlParams } from '@jtnews/shared/news';

import {
  PersonalSelectionItemDTO,
  PersonalSelectionLazyBlockDTO
} from './personal-selection.dto';
import {
  PersonalSelectionBlock,
  PersonalSelectionItemVM,
  TypeRecord
} from './personal-selection.vm';

const presentImage = (imageDTO: PersonalSelectionItemDTO['mainPhoto']) => {
  const defaultImg = {
    src: '',
    sources: [],
    hasCommercialLabel: false
  };
  const values = [
    {
      breakpoint: 375,
      width: 342
    },
    {
      breakpoint: 600,
      width: 566
    },
    {
      breakpoint: 899,
      width: 382
    },
    {
      breakpoint: 1279,
      width: 440
    },
    {
      breakpoint: 0,
      width: 316,
      noMedia: true
    }
  ];

  if (!guardUnspecified(imageDTO)) {
    return defaultImg;
  }

  const image = getResponsiveImageData({
    url: imageDTO.url,
    hasCommercialLabel: imageDTO.hasCommercialLabel,
    width: imageDTO.width,
    values
  });
  return guardUnspecified(image)
    ? {
        src: image.data.img.src,
        sources: image.data.sources,
        loading: image.data.img.loading,
        decoding: image.data.img.decoding,
        hasCommercialLabel: image.data.hasCommercialLabel
      }
    : defaultImg;
};

const presentReachGoals = (data: PersonalSelectionLazyBlockDTO['reach_goal']) => ({
  general: data?.general || [],
  view: data?.view || []
});

const presentPersonalSelectionItem = (
  itemDTO: PersonalSelectionItemDTO
): PersonalSelectionItemVM => ({
  id: itemDTO.id,
  header: removeHtmlTags(itemDTO.header),
  url: itemDTO.urls.url,
  comments: {
    count: itemDTO.commentsCount || 0,
    url:
      guardUnspecified(itemDTO.commentsCount) && itemDTO.commentsCount > 0
        ? itemDTO.urls.urlComments
        : addUrlParams(itemDTO.urls.urlComments, { discuss: '1' }),
    isAllowed: itemDTO.isCommentsAllowed ?? true
  },
  viewsCount: itemDTO.viewsCount,
  type: itemDTO.isCommercial ? TypeRecord.Commercial : TypeRecord.Editorial,
  image: presentImage(itemDTO.mainPhoto),
  formats: itemDTO.formats.map(item => ({
    url: item.url,
    id: item.id.toString(),
    text: item.name
  })),
  themes: itemDTO.themes.map(item => ({
    url: item.path,
    id: item.id.toString(),
    text: item.name
  })),
  rubrics: itemDTO.rubrics.map(item => ({
    url: item.path,
    id: item.id.toString(),
    text: item.name
  }))
});

export const presentPersonalSelectionBlock = (
  block: PersonalSelectionLazyBlockDTO
): PersonalSelectionBlock => {
  const { data, reach_goal: reachGoal, type } = block;
  return {
    data: guardUnspecified(data)
      ? data.map(item => presentPersonalSelectionItem(item))
      : [],
    reachGoal: presentReachGoals(reachGoal),
    type,
    isLazy: false,
    params: null,
    meta: null
  };
};
